import type { Transaction } from '@tiptap/pm/state'

// Origin classification for editor transactions, used to tell a genuinely-local
// forward edit apart from a change that arrived through Yjs (a remote peer, the
// collaborative UndoManager, or a forced re-render) or an undo/redo operation.
//
// y-prosemirror only distributes as a TRANSITIVE dependency here, so we read
// its meta by the PluginKey's generated string name rather than importing the
// plugin key. prosemirror-state's createKey() serializes the FIRST instance of
// a given name as the bare `"<name>$"` and only subsequent instances as
// `"$1"`, `"$2"`, etc. — so `new PluginKey('y-sync')` serializes to
// `"y-sync$"` (and the ProseMirror history plugin's singleton key to
// `"history$"`), moving to `"$1"`/`"$2"`/`"$3"` only if bundle ordering ever
// instantiates more than one. We probe the first few of each so classification
// survives bundle-order shifts.
//
// This module is the ONE undo/redo origin classifier — the hydration
// reconciler and the reject latch both read it, so the two surfaces can never
// again disagree about which meta keys mean "undo engine".

const Y_SYNC_META_KEYS = [
  'y-sync$',
  'y-sync$1',
  'y-sync$2',
  'y-sync$3',
] as const
const HISTORY_META_KEYS = [
  'history$',
  'history$1',
  'history$2',
  'history$3',
] as const

// True when the transaction was applied FROM Yjs rather than typed locally.
// y-prosemirror tags every such transaction on its sync plugin key with
// `isChangeOrigin: true` (remote peer edit, UndoManager undo/redo, or forced
// re-render); a local ProseMirror edit carries no y-sync meta at all.
export function isYjsOrigin(tr: Transaction): boolean {
  for (const key of Y_SYNC_META_KEYS) {
    const meta = tr.getMeta(key) as { isChangeOrigin?: boolean } | undefined
    if (meta?.isChangeOrigin === true) {
      return true
    }
  }
  return false
}

// True when the transaction is an undo OR redo produced by either undo engine:
// the Yjs/Liveblocks UndoManager (y-sync meta `isUndoRedoOperation: true`) or
// the ProseMirror history plugin (a `history$N` meta carrying a `redo`
// boolean, either direction). Direction-agnostic by design: the reject latch
// must treat both directions as non-forward edits, and the hydration
// reconciler derives direction from content presence (absent = undo,
// absent→present transition = redo), never from the meta.
export function isUndoRedoOperation(tr: Transaction): boolean {
  for (const key of Y_SYNC_META_KEYS) {
    const meta = tr.getMeta(key) as
      | { isUndoRedoOperation?: boolean }
      | undefined
    if (meta?.isUndoRedoOperation === true) {
      return true
    }
  }
  for (const key of HISTORY_META_KEYS) {
    const meta = tr.getMeta(key) as { redo?: boolean } | undefined
    if (meta !== undefined && typeof meta.redo === 'boolean') {
      return true
    }
  }
  return false
}

// A forward edit is a document change this client typed locally — not a Yjs
// change and not an undo/redo. Only these disarm the AI-proposal reject
// undo/redo latch; a foreign or undo/redo change must leave the latch intact.
export function isLocalForwardEdit(tr: Transaction): boolean {
  return tr.docChanged && !isYjsOrigin(tr) && !isUndoRedoOperation(tr)
}
